import { Component, OnDestroy, TemplateRef, ViewChild, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import {
  BehaviorSubject,
  EMPTY,
  catchError,
  finalize,
  filter,
  first,
  switchMap,
  takeUntil,
  tap,
  Subject,
} from 'rxjs';
import { ConfirmModalComponent } from 'src/app/core/components/confirm-modal/confirm-modal.component';
import { Constants } from 'src/app/core/constants';
import { CustomFile } from 'src/app/core/models/customFile.interface';
import { ErrorModel } from 'src/app/core/models/error-model.interface';
import { AuthorizedUserData } from 'src/app/core/models/user/auth-user.interface';
import { ProfileSettingsFormModel } from 'src/app/core/models/user/profile-settings-form.model';
import { AuthService } from 'src/app/core/services/auth.service';
import { FileService } from 'src/app/core/services/file.service';
import { ProfileService } from 'src/app/core/services/profile.service';
import { ToastService } from 'src/app/core/services/toast.service';
import { Convertor } from 'src/app/core/utils/convertor';
import { ObservableUtil } from 'src/app/core/utils/observable-util';
import { Util } from 'src/app/core/utils/util';

@Component({
    selector: 'app-profile',
    templateUrl: './profile-settings.component.html',
    styleUrls: ['./profile-settings.component.scss'],
    providers: [ProfileSettingsFormModel],
    standalone: false
})
export class ProfileSettingsComponent implements OnDestroy {
  private readonly profileService = inject(ProfileService);
  private readonly authService = inject(AuthService);
  private readonly fileService = inject(FileService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly modalService = inject(NgbModal);
  private readonly router = inject(Router);
  formModel = inject(ProfileSettingsFormModel);

  @ViewChild('dangerTpl')
  public dangerTpl!: TemplateRef<unknown>;

  @ViewChild('successTpl')
  public successTpl!: TemplateRef<unknown>;

  @ViewChild('apiSecretSuccessTpl')
  public apiSecretSuccessTpl!: TemplateRef<unknown>;

  public readonly imageExtensions = Constants.ImageExtensions;
  public readonly imageExtensionsLabel = Constants.ImageExtensions
    .split(',')
    .map((extension) => extension.toUpperCase())
    .join(', ');
  public readonly languages = Constants.SupportedLanguages;

  public profileImagePath = '';
  public prevImageExtension? = '';

  public avatar$ = new BehaviorSubject<CustomFile | null>(null);
  public avatarPreviewUrl: string | null = null;
  public currentAvatarUrl: string | null = null;
  public avatarInitials = '';

  // These fields just for ease of use on template
  public nameFormControl: FormControl<string | null>;
  public surnameFormControl: FormControl<string | null>;
  public birthDateFormControl: FormControl<Date | string | null>;
  public phoneNumberFormControl: FormControl<string | null>;
  public emailFormControl: FormControl<string>;

  public callbackError?: string;
  public apiSecret = '';
  public isGeneratingApiSecret = false;

  private profileSettings$ = this.profileService.getProfileSettings();
  private readonly destroy$ = new Subject<void>();
  private avatarChangeMode: 'unchanged' | 'uploaded' | 'removed' = 'unchanged';

  constructor() {
    const formModel = this.formModel;

    this.nameFormControl = formModel.form.controls.name;
    this.surnameFormControl = formModel.form.controls.surname;
    this.birthDateFormControl = formModel.form.controls.birthDate;
    this.phoneNumberFormControl = formModel.form.controls.phoneNumber;
    this.emailFormControl = formModel.form.controls.email;

    const userData = this.authService.user$.getValue().data;

    if (userData) {
      this.profileImagePath = `users/${userData.id}`;
      this.prevImageExtension = userData.avatarImageExtension;
    }

    this.syncAvatarInitials();

    this.profileSettings$
      .pipe(
        first(),
        tap((settings) => {
          this.formModel.form.patchValue(settings);
          this.apiSecret = settings.apiSecret ?? '';
        }),
      )
      .subscribe();

    this.nameFormControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.syncAvatarInitials());

    this.surnameFormControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.syncAvatarInitials());

    this.loadCurrentAvatar();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public saveChanges(): void {
    const extensionName = Util.getExtensionFromFileTitle(
      this.avatar$.getValue()?.file.name,
    );
    const newImageExtension = extensionName ? `.${extensionName}` : undefined;
    const userData = this.authService.user$.getValue().data;
    const avatarImageExtension =
      this.avatarChangeMode === 'uploaded'
        ? newImageExtension
        : this.avatarChangeMode === 'removed'
          ? ''
          : undefined;

    this.profileService
      .updateProfile({
        ...this.formModel.form.getRawValue(),
        avatarImageExtension,
      })
      .pipe(
        first(),
        tap(() => {
          const nextAvatarImageExtension =
            this.avatarChangeMode === 'removed'
              ? undefined
              : this.avatarChangeMode === 'uploaded'
                ? newImageExtension
                : userData?.avatarImageExtension;

          const updatedUserData: AuthorizedUserData = {
            id: userData!.id,
            userName: userData!.userName,
            fullName: Util.getFullName(
              this.nameFormControl.value,
              this.surnameFormControl.value,
            ),
            role: userData!.role,
            avatarImageExtension: nextAvatarImageExtension,
            interfaceLanguage:
              this.formModel.form.controls.interfaceLanguage.value,
          };

          this.authService.updateUserData(updatedUserData);
          this.prevImageExtension = nextAvatarImageExtension;

          if (this.avatarChangeMode === 'removed') {
            this.currentAvatarUrl = null;
            this.avatarPreviewUrl = null;
          }

          this.avatarChangeMode = 'unchanged';

          this.toastService.show({
            template: this.successTpl,
            className: 'bg-success text-light',
          });

          this.formModel.form.markAsPristine();
        }),
        catchError((error) => {
          const errorModel = error.error as ErrorModel;

          const i128nKey = Convertor.pascalToScreamingSnakeCase(
            errorModel.i18nKey,
          );
          const translatedMessage = this.translateService.instant(
            `ERRORS.${i128nKey}`,
          );

          this.callbackError = translatedMessage;

          this.toastService.show({
            template: this.dangerTpl,
            className: 'bg-danger text-light',
          });

          return EMPTY;
        }),
      )
      .subscribe();
  }

  public deleteProfile(): void {
    const modalRef = this.modalService.open(ConfirmModalComponent, {
      size: 'sm',
    });

    modalRef.componentInstance.message = Util.translateWithParam(
      this.translateService,
      `WARNINGS.DELETE`,
      {
        uk: 'свій профіль',
        en: 'your profile',
      },
    );

    modalRef.closed
      .pipe(
        first(),
        filter((res) => !!res),
        switchMap(() => this.profileService.deleteProfile()),
        switchMap(() =>
          this.fileService.delete(
            `users/${this.authService.user$.getValue().data?.id}`,
          ),
        ),
        tap(() => this.router.navigateByUrl(`login`)),
        catchError((error) => {
          console.log(error);
          return EMPTY;
        }),
      )
      .subscribe();
  }

  public onUploadFile(event: CustomFile): void {
    this.avatar$.next(event);
    this.avatarChangeMode = 'uploaded';
    this.currentAvatarUrl = null;

    ObservableUtil.getFilePreview$(event.file)
      .pipe(
        first(),
        tap((previewUrl) => {
          this.avatarPreviewUrl = previewUrl;
        }),
      )
      .subscribe();

    this.saveChanges();
  }

  public onDeleteUploadedAvatar(): void {
    this.avatar$.next(null);
    this.avatarPreviewUrl = null;
    this.currentAvatarUrl = null;
    this.avatarChangeMode = 'removed';

    this.saveChanges();
  }

  public removeAvatar(): void {
    const avatarPath = this.getAvatarPath();

    if (!avatarPath) {
      this.avatar$.next(null);
      this.avatarPreviewUrl = null;
      this.currentAvatarUrl = null;
      this.avatarChangeMode = 'removed';
      this.saveChanges();
      return;
    }

    this.fileService
      .delete(avatarPath)
      .pipe(
        first(),
        tap(() => {
          this.avatar$.next(null);
          this.avatarPreviewUrl = null;
          this.currentAvatarUrl = null;
          this.avatarChangeMode = 'removed';
          this.saveChanges();
        }),
        catchError(() => {
          this.callbackError = this.translateService.instant('ERRORS.DELETE_AVATAR');
          this.toastService.show({
            template: this.dangerTpl,
            className: 'bg-danger text-light',
          });

          return EMPTY;
        }),
      )
      .subscribe();
  }

  public generateApiSecret(): void {
    this.isGeneratingApiSecret = true;

    this.authService
      .generateSecretCredential()
      .pipe(
        first(),
        tap((response) => {
          this.apiSecret = response.secretKey;
          this.toastService.show({
            template: this.apiSecretSuccessTpl,
            className: 'bg-success text-light',
          });
        }),
        catchError(() => {
          this.callbackError = this.translateService.instant(
            'ERRORS.GENERATE_API_SECRET',
          );

          this.toastService.show({
            template: this.dangerTpl,
            className: 'bg-danger text-light',
          });

          return EMPTY;
        }),
        finalize(() => {
          this.isGeneratingApiSecret = false;
        }),
      )
      .subscribe();
  }

  private loadCurrentAvatar(): void {
    const avatarPath = this.getAvatarPath();

    if (!avatarPath) {
      this.currentAvatarUrl = null;
      return;
    }

    this.fileService
      .getFileUrl(avatarPath, true)
      .pipe(
        first(),
        tap((url) => {
          this.currentAvatarUrl = url !== Constants.NotFound ? url : null;
        }),
      )
      .subscribe();
  }

  private getAvatarPath(): string | null {
    if (!this.profileImagePath || !this.prevImageExtension) {
      return null;
    }

    return `${this.profileImagePath}/avatar${this.prevImageExtension}`;
  }

  private syncAvatarInitials(): void {
    const fullName = Util.getFullName(
      this.nameFormControl.value,
      this.surnameFormControl.value,
    );

    this.avatarInitials = (fullName ?? this.authService.user$.getValue().data?.userName ?? '')
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word.charAt(0))
      .join('')
      .toUpperCase();
  }
}
